import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Science Wednesday: Towards a computational model of poetry generation",
  "description": "",
  "published": "2012-03-07T00:00:00.000Z",
  "redirect_from": ["/blog/science-wednesday-towards-a-computational-model-of-poetry-generation/swizec/3855"],
  "categories": "art, Ljubljana, Poetry, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/300px-Oscar_Wilde_portrait_by_Napoleon_Sarony_-_albumen4.jpg",
        "alt": "English: Three quarter length portrait of Osca...",
        "title": "English: Three quarter length portrait of Osca..."
      }}></img></p>
    <p><a parentName="p" {...{
        "href": "https://www.era.lib.ed.ac.uk/handle/1842/3460",
        "title": "Towards a computational model of poetry generation"
      }}>{`Towards A Computational Model of Poetry Generation`}</a>{` is a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Paper",
        "title": "Paper"
      }}>{`paper`}</a>{` by Manurung, Ritchie and Thompson (whomever they are) published in May 2000 and so far seems to be the best starting point for `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/Le-Thesis"
      }}>{`my graduation thesis`}</a>{`.`}</p>
    <p>{`There are three main parts to this story:`}</p>
    <ol>
      <li parentName="ol">{`why!?`}</li>
      <li parentName="ol">{`what makes it hard`}</li>
      <li parentName="ol">{`how it used to be done`}</li>
      <li parentName="ol">{`how it should be done`}</li>
    </ol>
    <p>{`Obviously my idea of how it should be done matches somewhat with the authors of this paper, otherwise I wouldn't hold it in such a high regard :P`}</p>
    <h2 {...{
      "id": "why"
    }}>{`Why!?`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Poetry is a unique artifact of human `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Natural_language",
          "title": "Natural language"
        }}>{`natural language`}</a>{` pro- duction, with the distinctive feature of having a strong unity between its content and its form. The creation of poetry is a task that requires intelligence, expert mastery over world and linguistic knowledge, and creativity. Al- though some researchwork has been devoted towards cre- ative language such as story generation, poetry writing has not been afforded the same attention. It is the aim of this research to fill that gap, and to shed some light on what often seems to be the most enigmatic andmysterious forms of `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Art",
          "title": "Art"
        }}>{`artistic expression`}</a>{`.`}</p>
    </blockquote>
    <p>{`In short, this is a really cool problem to solve because it's something that hasn't `}<em parentName="p">{`really`}</em>{` been done before. And it just looks interesting when you give computers - strictly logical reasonable machines - even the most modest of abilities to create art.`}</p>
    <p>{`When rationalization is required ... well somebody needs to create all those pop songs. Imagine if we could get a computer to write them, then sell them to all the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Pop_music",
        "title": "Pop music"
      }}>{`pop music`}</a>{` labels to feed to the Spears and Aguileras of this world. Marvelous!`}</p>
    <h2 {...{
      "id": "what-makes-it-hard"
    }}>{`What makes it hard`}</h2>
    <p>{`The problem with poetry is two-fold. On the one hand one finds themselves at a serious disadvantage in comparison to creating a traditional `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Natural_language_generation",
        "title": "Natural language generation"
      }}>{`NLG`}</a>{`(natural language generation) tool because poetry is less rigid. There is no message you are trying to convey and so the goal you are trying to achieve is a bit muddy.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/300px-Ginsberg-dylan2.jpg",
        "alt": "English: Portrait of Allen Ginsberg and Bob Dy...",
        "title": "English: Portrait of Allen Ginsberg and Bob Dy..."
      }}></img></p>
    <p>{`Furthermore, poetry possesses a certain unity between message and form. As Levin stated in 1962 `}<em parentName="p">{`"In poetry the form of the discourse and its meaning are fused into a higher unity"`}</em>{`.`}</p>
    <p>{`This creates a problem because traditional NLG systems are decomposed into simpler units of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Content_determination",
        "title": "Content determination"
      }}>{`content determination`}</a>{`, text planning and surface realisation. In `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Poetry",
        "title": "Poetry"
      }}>{`poetry form`}</a>{` and message are too intertwined to allow this approach.`}</p>
    <p>{`The only advantage we might have from creating poetry instead of informative text, is that readers expect to do a lot of interpretative work, but realistically this again only serves to make it more difficult to define a goal for our `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Algorithm",
        "title": "Algorithm"
      }}>{`algorithm`}</a>{`.`}</p>
    <p>{`Essentially: it's hard to decide what we `}{`_`}{`want `}{`_`}{`and traditional well researched approaches break down. Great.`}</p>
    <h2 {...{
      "id": "how-it-used-to-be-done"
    }}>{`How it used to be done`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/300px-Slovenska_lipa_srebrnik_reverz1.jpg",
        "alt": "Slovenska lipa srebrnik reverz",
        "title": "Slovenska lipa srebrnik reverz"
      }}></img></p>
    <p>{`As with a lot of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Natural_language_processing",
        "title": "Natural language processing"
      }}>{`NLP`}</a>{`research, there were several attempts at a strictly academic approach to poetry generation back in the 80's. More recently, there have been several-ish web based attempts, but using similar techniques.`}</p>
    <p>{`The problem with old approaches is that they were basically party tricks - humans generated extremely detailed grammars and poetic structures, so the computer ended up just semi-randomly filling in the words. While this produced good looking poetry and in case of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Racter",
        "title": "Racter"
      }}>{`RACTER`}</a>{` even lead to publication.`}</p>
    <p>{`Leaving aside considerations of whether the poetry at this point is even computer generated, all of these approaches completely ignored semantic meaning and most poetics as well - rhythm, rhyme and figurative language.`}</p>
    <h2 {...{
      "id": "how-it-should-be-done"
    }}>{`How it should be done`}</h2>
    <p>{`Just as I'm planning to in my thesis, authors of this paper have decided to focus mostly on poetic form and ensure their results follow a strict verse, rhyming structure and other features usually attributed to classic poetry. Mostly because it is easy to pass off almost anything as modern experimental poetry and the more structure we can muster, the easier it becomes to actually verify results.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/03/Jim_Morrison_19702.jpg",
        "alt": "English: Jim Morrison in 1970.",
        "title": "English: Jim Morrison in 1970."
      }}></img></p>
    <p>{`The approach they suggest is a stochastic hillclimbing algorithm - particularly an evolutionary approach where the algorithm follows this kind of loop:`}</p>
    <ol>
      <li parentName="ol">{`Generate`}</li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "http://en.wikipedia.org/wiki/Evaluation",
          "title": "Evaluation"
        }}>{`Evaluate`}</a></li>
    </ol>
    <p>{`Generation is done by mutating the best candidates from the previous cycle through three simple constructs: Adding, Changing and Deleting.`}</p>
    <p>{`So if you had a verse `}<em parentName="p">{`"John walked"`}</em>{` it could become `}<em parentName="p">{`"John walked `}<strong parentName="em">{`to the store`}</strong>{`"`}</em>{`, or `}<em parentName="p">{`"John `}<strong parentName="em">{`lumbered`}</strong>{`".`}</em>{` Or for instance `}<em parentName="p">{`"John likes Jill and Mary`}</em>{`" it becomes `}<em parentName="p">{`"John likes Jill"`}</em>{`.`}</p>
    <p>{`Because of an integrated architecture (combining all of semantics, form, etc.) these changes can and should happen at any level so special care must be taken that changing the semantics of a verse, doesn't negatively affect its rhythm. Although from the paper I don't grok why exactly this is a problem, since the evaluation step should take care of negative mutations.`}</p>
    <p>{`After we have a new population, these are then evaluated for correctness and the candidates with highest scores from the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Fitness_function",
        "title": "Fitness function"
      }}>{`fitness functions`}</a>{` get to go into the next round of mutations.`}</p>
    <p>{`The authors go into little detail about assessing the different poetic structures, mostly because they have not yet cracked how to implement all of them. Detecting rhythm seems to be where they've made the most progress and a solution is suggested where we give the algorithm a target `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Phonetic_form",
        "title": "Phonetic form"
      }}>{`phonetic form`}</a>{`, like this `}<em parentName="p">{`limerick`}</em>{`:`}</p>
    <p>{`w,s,w,w,s,w,w,s(a)
w,s,w,w,s,w,w,s(a)
w,s,w,w,s(b)
w,s,w,w,s(b)
w,s,w,w,s,w,w,s(a)`}</p>
    <p>{`Where `}<em parentName="p">{`w`}</em>{` means a weak stress syllable and `}<em parentName="p">{`s`}</em>{` means a strong one. In parentheses the rhyming structure is described.`}</p>
    <p>{`It is suggested that with the exception figurative language all the criteria can be dumbed down to numerical arguments that can be easily quantified through different means that are only loosely described.`}</p>
    <p>{`They also go into a bit of formal detail explaining the sort of grammars that are used, but you should go read the actual paper if you're interested in such heavily theoretical things :)`}</p>
    <p>{`Here is an example of what their algorithm can produce:`}</p>
    <p><strong parentName="p">{`Target semantics`}</strong></p>
    <p>{`{
john(1),mary(2), dog(3), bottle(4), love(5,6,7), slow(8), smile(9,10)
}`}</p>
    <p><strong parentName="p">{`Target Form`}</strong></p>
    <p>{`w,s,w,w,s,w,w,s,
w,s,w,w,s,w,w,s`}</p>
    <p><strong parentName="p">{`Surface`}</strong></p>
    <p>{`the bottle was loved by Luke
a bottle was loved by a dog`}</p>
    <p><strong parentName="p">{`Stress`}</strong></p>
    <p>{`w,s,w,w,s,w,s,
w,s,w,w,s,w,w,s`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://betsyandrewsetchart.wordpress.com/2011/11/29/hoaku-of-poetry-and-politics/"
        }}>{`HOAku (Of Poetry and Politics)`}</a>{` (betsyandrewsetchart.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://ceruleanmoon.wordpress.com/2012/02/26/faint-sigh/"
        }}>{`Faint Sigh`}</a>{` (ceruleanmoon.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://combatsteve.wordpress.com/2012/02/07/slight-delay-with-hints-of-things-to-come/"
        }}>{`Slight delay, with hints of things to come.`}</a>{` (combatsteve.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://navigatingcyberloss.wordpress.com/2012/03/03/im-no-shakespeare-but/"
        }}>{`I'm no Shakespeare, but...`}</a>{` (navigatingcyberloss.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://ibawordmonger.wordpress.com/2012/02/07/my-thoughts-on-poetry-writing/"
        }}>{`My Thoughts on Poetry & Writing`}</a>{` (ibawordmonger.wordpress.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=8ef58f03-3863-4b67-a428-337537dccf67",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      